/**
 * @lodex-pro/web-portal/public/DisposalCheckYourEmail — post-registration confirmation
 * state for the disposal-portal flow (DEC-160).
 *
 * Mirrors CheckYourEmail (operator, DEC-155) with three disposal-specific adaptations:
 *
 *   1. Renders the SWD payment-activation amber banner (§49: "no free tier";
 *      registration creates PAYMENT_PENDING account — portal access begins after
 *      payment activation).
 *   2. Uses `useDisposalMagicLinkIssueMutation` instead of the operator hook.
 *   3. Renders differentiated confirmation copy per `licenseMatchStatus`
 *      (MATCHED / UNMATCHED / NOT_PROVIDED) returned by the register response.
 *
 * Props:
 *   email — the email address that was registered.
 *   licenseMatchStatus — from DisposalRegistrationResponse; drives which
 *     verification-timing message to render.
 *
 * The resend button is disabled until a fresh Turnstile token is captured
 * (Turnstile tokens are single-use; each resend needs a fresh solve).
 *
 * Spec: DEC-156, DEC-160.
 */

import React, { useRef, useState } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useDisposalMagicLinkIssueMutation } from '../hooks/useDisposalMagicLinkIssueMutation';
import { PublicApiError } from './publicApi';

type LicenseMatchStatus = 'MATCHED' | 'UNMATCHED' | 'NOT_PROVIDED';

interface DisposalCheckYourEmailProps {
  readonly email: string;
  readonly licenseMatchStatus: LicenseMatchStatus;
}

function licenseMatchCopy(status: LicenseMatchStatus): string {
  switch (status) {
    case 'MATCHED':
      return 'We matched your facility to the state license catalog. This speeds up admin approval.';
    case 'UNMATCHED':
      return "Your facility isn't in our catalog yet. An admin will verify your registration manually. This may take an extra business day.";
    case 'NOT_PROVIDED':
      return 'No license number provided. An admin will contact you for verification.';
  }
}

export function DisposalCheckYourEmail({
  email,
  licenseMatchStatus,
}: DisposalCheckYourEmailProps): React.JSX.Element {
  const [resendToken, setResendToken] = useState<string | null>(null);
  const [resendBanner, setResendBanner] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);
  const resendRef = useRef<TurnstileInstance>(null);

  const issueMutation = useDisposalMagicLinkIssueMutation();

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  const handleResend = (): void => {
    if (resendToken === null) return;
    setResendBanner(null);

    issueMutation.mutate(
      { email, captchaToken: resendToken },
      {
        onSuccess: () => {
          setResendSuccess(true);
          setResendToken(null);
          resendRef.current?.reset();
        },
        onError: (err) => {
          setResendToken(null);
          resendRef.current?.reset();
          if (err instanceof PublicApiError) {
            if (err.code === 'RATE_LIMIT_EXCEEDED') {
              const seconds = err.retryAfterSeconds;
              setResendBanner(
                seconds !== undefined
                  ? `Too many attempts. Try again in ${seconds} seconds.`
                  : 'Too many attempts. Try again shortly.',
              );
            } else if (
              err.code === 'CAPTCHA_REQUIRED' ||
              err.code === 'CAPTCHA_VERIFICATION_FAILED'
            ) {
              setResendBanner('Security check failed. Please try again.');
            } else {
              setResendBanner('Something went wrong. Try again.');
            }
          } else {
            setResendBanner('Something went wrong. Try again.');
          }
        },
      },
    );
  };

  return (
    <div>
      <h1
        style={{
          fontSize: '22px',
          fontWeight: 700,
          marginBottom: '8px',
          color: 'var(--color-text-primary)',
        }}
      >
        Check your email
      </h1>

      <p style={{ fontSize: '16px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
        We sent a verification link to{' '}
        <strong style={{ color: 'var(--color-text-primary)' }}>{email}</strong>. Click the link in
        that email to complete your registration.
      </p>

      <p
        role="note"
        style={{
          background: 'rgba(246, 182, 94, 0.14)',
          borderLeft: '3px solid var(--color-warning)',
          padding: '12px 16px',
          marginBottom: '16px',
          color: 'var(--color-warning)',
          fontSize: '14px',
          lineHeight: '1.5',
        }}
      >
        <strong>SWD portal activation is a two-step process.</strong> After Paelode admin approves
        your registration, a representative will contact you to complete billing setup. Portal
        access begins after payment activation.
      </p>

      <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}>
        {licenseMatchCopy(licenseMatchStatus)}
      </p>

      <p style={{ fontSize: '14px', color: 'var(--color-text-tertiary)', marginBottom: '32px' }}>
        Don&apos;t see the email? Check your spam or junk folder. Verification links expire after 15
        minutes.
      </p>

      <hr
        style={{
          border: 'none',
          borderTop: '1px solid var(--color-border-primary)',
          marginBottom: '24px',
        }}
      />

      <h2
        style={{
          fontSize: '16px',
          fontWeight: 600,
          marginBottom: '12px',
          color: 'var(--color-text-secondary)',
        }}
      >
        Resend verification link
      </h2>

      {resendSuccess ? (
        <p role="status" style={{ color: 'var(--color-success)', fontSize: '15px' }}>
          New link sent. Check your inbox.
        </p>
      ) : (
        <>
          {resendBanner !== null && (
            <p
              role="alert"
              style={{ color: 'var(--color-danger)', fontSize: '14px', marginBottom: '12px' }}
            >
              {resendBanner}
            </p>
          )}

          <div style={{ marginBottom: '12px' }}>
            <Turnstile
              ref={resendRef}
              siteKey={siteKey}
              onSuccess={(token) => {
                setResendToken(token);
              }}
              onError={() => {
                setResendToken(null);
              }}
              onExpire={() => {
                setResendToken(null);
              }}
              options={{ theme: 'dark' }}
            />
          </div>

          <button
            type="button"
            onClick={handleResend}
            disabled={resendToken === null || issueMutation.isPending}
            style={{
              minHeight: '56px',
              padding: '14px 24px',
              background:
                resendToken !== null ? 'var(--color-accent-blue)' : 'var(--color-bg-tertiary)',
              color: resendToken !== null ? 'var(--color-on-accent)' : 'var(--color-text-tertiary)',
              border: 'none',
              borderRadius: 'var(--radius-pill)',
              fontSize: '16px',
              fontWeight: 600,
              cursor: resendToken !== null ? 'pointer' : 'not-allowed',
              width: '100%',
            }}
          >
            {issueMutation.isPending ? 'Sending…' : 'Resend link'}
          </button>
        </>
      )}
    </div>
  );
}
